import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import LocationPicker from './LocationPicker';
import TmdbSearch, { forgetSearches, sourceError } from './TmdbSearch';
import { useLocations } from '../locations';
import { fieldOptionLabel, optionValue, uiLang } from '../kinds';
import { Alert, Button, Dialog, SegmentedControl, Select, TextField } from '../ui';
import { FilmIcon, WarningIcon } from '../ui/icons';

/**
 * Adding a film or a series (an item type made from the movie or TV
 * model): search TMDB as you type, pick a result, set what TMDB does not
 * know (format, edition, place), add. The item is filled from TMDB by the
 * server through the type's integration, poster included. Results already
 * in a collection the member can read say so, and adding one again needs
 * "Add anyway". "Enter by hand" opens the usual form; "Scan" the scanner.
 */
function AddFromSourceModal({ inventory, itemType, onAdded, onManual, onScan, scanLabel, onClose }) {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n);
  const kind = itemType.preset === 'tv' ? 'tv' : 'movie';
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [support, setSupport] = useState('');
  const [edition, setEdition] = useState('');
  const [locationId, setLocationId] = useState(null);
  const [adding, setAdding] = useState(false);

  const hasLocations = !!inventory.location_type_id;
  const { places } = useLocations(inventory.location_type_id);
  // The format and edition of the copy, unless the owner put those fields aside.
  const supportField = useMemo(() => (itemType.fields || []).find(field => field.key === 'support' && field.type === 'select' && !field.unused), [itemType]);
  const editionField = useMemo(() => (itemType.fields || []).find(field => field.key === 'edition' && !field.unused), [itemType]);

  const select = (result) => {
    setSelected(result);
    setError(null);
  };

  const add = async () => {
    if (!selected) return;
    setAdding(true);
    setError(null);
    const values = {};
    if (support) values.support = support;
    if (edition.trim()) values.edition = edition.trim();
    try {
      const response = await api.post(`/inventories/${inventory.id}/items/from-source`, {
        source: 'tmdb',
        tmdb_id: selected.tmdb_id,
        item_type_id: itemType.id,
        lang,
        values,
        ...(hasLocations ? { location_id: locationId } : {}),
      });
      forgetSearches();   // what is owned has just changed
      onAdded(response.data);
    } catch (err) {
      const code = err.response?.data?.code;
      setError(code === 'invalid_value' && err.response?.data?.field === 'location'
        ? t('locations.errors.invalid_place')
        : sourceError(t, err, itemType.fields || []));
      setAdding(false);
    }
  };

  const owned = selected?.owned || [];
  const title = (result) => `${result.title}${result.year ? ` (${result.year})` : ''}`;

  return (
    <Dialog
      open
      onClose={onClose}
      size="xl"
      title={t(kind === 'tv' ? 'addFromSource.titleTv' : 'addFromSource.titleMovie')}
      description={t('addFromSource.into', { name: inventory.name, type: itemType.name })}
      className="add-source"
      footer={(
        <>
          <p className="add-source__attribution">{t('addFromSource.attribution')}</p>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={add} disabled={!selected} loading={adding} icon={owned.length > 0 ? WarningIcon : undefined}>
            {owned.length > 0 ? t('addFromSource.addAnyway') : t('addFromSource.add')}
          </Button>
        </>
      )}
    >
      <SegmentedControl
        label={t('addFromSource.how')}
        value="search"
        onChange={(id) => {
          if (id === 'manual') onManual();
          if (id === 'scan' && onScan) onScan();
        }}
        options={[
          { id: 'search', label: t('addFromSource.searchTab') },
          // The scanner (phase 4 §5): a box's EAN, answered from the codes the installation learned.
          ...(onScan ? [{ id: 'scan', label: scanLabel || t('addBook.scanTab') }] : []),
          { id: 'manual', label: t('addFromSource.manualTab') },
        ]}
        className="add-source__how"
      />

      {error && <Alert tone="danger">{error}</Alert>}

      <div className={`add-source__layout${selected ? ' has-selection' : ''}`}>
        <TmdbSearch kind={kind} lang={lang} selected={selected} onSelect={select} onManual={onManual} />

        {selected ? (
          <section aria-label={t('addFromSource.preview')} className="add-source__preview">
            <div className="add-source__summary">
              {selected.poster_thumb
                ? <img src={selected.poster_thumb} alt="" className="add-source__poster" />
                : <span className="add-source__poster source-poster--empty" aria-hidden="true" />}
              <div>
                <h3 className="add-source__title">{title(selected)}</h3>
                {selected.overview && <p className="add-source__overview">{selected.overview}</p>}
              </div>
            </div>

            {owned.length > 0 && (
              <Alert tone="warning" title={t('addFromSource.duplicateTitle')}>
                <ul className="add-source__owned">
                  {owned.map(item => (
                    <li key={item.item_id}>
                      <Link to={`/c/${item.inventory_id}/items/${item.item_id}/edit`}>{item.title || t('addFromSource.untitled')}</Link>
                      {' — '}{item.inventory_name}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            <div className="add-source__fields">
              {supportField && (
                <Select label={supportField.name} value={support} onChange={(e) => setSupport(e.target.value)}>
                  <option value="">{t('itemForm.selectOption')}</option>
                  {(supportField.options || []).map(option => (
                    <option key={optionValue(option)} value={optionValue(option)}>{fieldOptionLabel(supportField, option, lang)}</option>
                  ))}
                </Select>
              )}
              {editionField && (
                <TextField label={editionField.name} value={edition} onChange={(e) => setEdition(e.target.value)} />
              )}
            </div>
            {hasLocations && (
              <div className="item-field">
                <label htmlFor="source-location" className="ui-field__label">{t('collection.location')}</label>
                <LocationPicker inputId="source-location" places={places} value={locationId} onChange={setLocationId} />
              </div>
            )}
            <p className="ui-field__hint">{t('addFromSource.filledFromTmdb')}</p>
          </section>
        ) : (
          <div className="add-source__hint" aria-hidden="true">
            <FilmIcon size={28} strokeWidth={1.6} />
            <p>{t('addFromSource.pickHint')}</p>
          </div>
        )}
      </div>
    </Dialog>
  );
}

export default AddFromSourceModal;
